import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { freelancerTaskAPI } from '../services/api';

const MAX_PHOTOS = 5;

function TaskPage() {
  const { token } = useParams();

  const [step, setStep] = useState('verify'); // verify | ticket | photos | signature | done
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [ticket, setTicket] = useState(null);
  const [proofId, setProofId] = useState(null);
  const [photos, setPhotos] = useState([]); // { id, url }
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [notes, setNotes] = useState('');
  const [visitNoteText, setVisitNoteText] = useState('');
  const [visitNotePhoto, setVisitNotePhoto] = useState(null);
  const [submittingNote, setSubmittingNote] = useState(false);

  const fileInputRef = useRef(null);
  const visitNoteFileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasSignatureRef = useRef(false);

  // Handle expired/invalid link states up front
  const [linkState, setLinkState] = useState(null); // null | 'expired' | 'revoked' | 'completed' | 'invalid'

  // Best-effort location capture — resolves with nulls if unavailable/denied/timed out,
  // never blocks or rejects, so the freelancer's submission is never held up by GPS.
  const getLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
        () => resolve({ latitude: null, longitude: null }),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
      );
    });
  };

  const handleApiError = (err, fallbackMsg) => {
    const status = err.response?.status;
    const msg = err.response?.data?.message || fallbackMsg;
    if (status === 410) {
      setLinkState(
        msg.toLowerCase().includes('expired') ? 'expired'
        : msg.toLowerCase().includes('revoked') ? 'revoked'
        : msg.toLowerCase().includes('completed') ? 'completed'
        : 'invalid'
      );
    } else {
      setError(msg);
    }
  };

  // Step 1: Verify phone
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      await freelancerTaskAPI.verify(token, phone);
      const res = await freelancerTaskAPI.getTicket(token, phone);
      setTicket(res.data.data.ticket);
      if (res.data.data.proof_id) {
        setProofId(res.data.data.proof_id);
      }
      setStep('ticket');
    } catch (err) {
      handleApiError(err, 'Verification failed. Please check your phone number.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 -> 3: Start task (creates proof record)
  const handleStartTask = async () => {
    setError('');
    setLoading(true);
    try {
      const loc = await getLocation();
      const res = await freelancerTaskAPI.createProof(token, {
        phone,
        location_address: ticket?.address || '',
        latitude: loc.latitude,
        longitude: loc.longitude,
      });
      setProofId(res.data.data.proof_id);
      setStep('photos');
    } catch (err) {
      handleApiError(err, 'Could not start task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Photo upload
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      setError('You can add up to ' + MAX_PHOTOS + ' photos');
      return;
    }
    setError('');
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('phone', phone);
      formData.append('image_type', 'site_photo');
      formData.append('image', file);
      const res = await freelancerTaskAPI.uploadImage(token, proofId, formData);
      setPhotos((prev) => [...prev, {
        id: res.data.data.image_id,
        url: res.data.data.image_url,
      }]);
    } catch (err) {
      handleApiError(err, 'Photo upload failed. Please try again.');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Step 4: Signature pad
  useEffect(() => {
    if (step !== 'signature') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#0a2540';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';

    const getPos = (evt) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
      const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
      return {
        x: (clientX - rect.left) * (canvas.width / rect.width),
        y: (clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const start = (evt) => {
      evt.preventDefault();
      drawingRef.current = true;
      hasSignatureRef.current = true;
      const pos = getPos(evt);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };
    const move = (evt) => {
      if (!drawingRef.current) return;
      evt.preventDefault();
      const pos = getPos(evt);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    };
    const end = () => { drawingRef.current = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);

    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [step]);

  const handleClearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasSignatureRef.current = false;
  };

  const handleSubmitSignature = async () => {
    if (!hasSignatureRef.current) {
      setError('Please provide a signature before submitting');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const loc = await getLocation();
      const canvas = canvasRef.current;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      const formData = new FormData();
      formData.append('phone', phone);
      formData.append('customer_name', ticket?.customer_name || '');
      formData.append('engineer_notes', notes || '');
      if (loc.latitude != null) formData.append('latitude', loc.latitude);
      if (loc.longitude != null) formData.append('longitude', loc.longitude);
      formData.append('signature', blob, 'signature.png');
      await freelancerTaskAPI.uploadSignature(token, proofId, formData);
      setStep('done');
    } catch (err) {
      handleApiError(err, 'Signature submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Visit note (can't complete right now) — link stays active for return visits
  const handleVisitNotePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setVisitNotePhoto(file);
  };

  const handleSubmitVisitNote = async () => {
    if (!visitNoteText || !visitNoteText.trim()) {
      setError('Please describe what happened');
      return;
    }
    setError('');
    setSubmittingNote(true);
    try {
      const loc = await getLocation();
      const formData = new FormData();
      formData.append('phone', phone);
      formData.append('note', visitNoteText.trim());
      if (loc.latitude != null) formData.append('latitude', loc.latitude);
      if (loc.longitude != null) formData.append('longitude', loc.longitude);
      if (visitNotePhoto) formData.append('image', visitNotePhoto);
      await freelancerTaskAPI.submitVisitNote(token, formData);
      setStep('note-done');
    } catch (err) {
      handleApiError(err, 'Could not submit note. Please try again.');
    } finally {
      setSubmittingNote(false);
    }
  };

  const styles = {
    page: {
      minHeight: '100vh',
      background: '#0a1420',
      color: '#e8edf2',
      fontFamily: "'DM Mono', monospace",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '24px 16px',
    },
    card: {
      width: '100%',
      maxWidth: 440,
      background: '#111f30',
      border: '1px solid #1e3349',
      borderRadius: 12,
      padding: 24,
      marginTop: 16,
    },
    logo: {
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: 1,
      color: '#4fd1ff',
      textAlign: 'center',
    },
    tagline: {
      fontSize: 11,
      color: '#7d94a8',
      textAlign: 'center',
      letterSpacing: 2,
      marginTop: 2,
    },
    label: {
      fontSize: 13,
      color: '#9db3c4',
      marginBottom: 6,
      display: 'block',
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      background: '#0a1420',
      border: '1px solid #2a4258',
      borderRadius: 8,
      color: '#e8edf2',
      fontSize: 16,
      fontFamily: "'DM Mono', monospace",
      boxSizing: 'border-box',
    },
    button: {
      width: '100%',
      padding: '13px 14px',
      background: '#4fd1ff',
      color: '#06121e',
      border: 'none',
      borderRadius: 8,
      fontSize: 15,
      fontWeight: 700,
      cursor: 'pointer',
      marginTop: 16,
    },
    buttonSecondary: {
      width: '100%',
      padding: '13px 14px',
      background: 'transparent',
      color: '#4fd1ff',
      border: '1px solid #2a4258',
      borderRadius: 8,
      fontSize: 14,
      cursor: 'pointer',
      marginTop: 10,
    },
    error: {
      color: '#ff6b6b',
      fontSize: 13,
      marginTop: 10,
      textAlign: 'center',
    },
    ticketRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '10px 0',
      borderBottom: '1px solid #1e3349',
      fontSize: 14,
    },
    ticketLabel: { color: '#7d94a8' },
    ticketValue: { color: '#e8edf2', textAlign: 'right', maxWidth: '65%' },
    instructionsBox: {
      marginTop: 16,
      padding: 14,
      background: '#1a2b3d',
      borderRadius: 8,
      borderLeft: '3px solid #4fd1ff',
      fontSize: 13.5,
      lineHeight: 1.5,
    },
    photoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 10,
      marginTop: 16,
    },
    photoThumb: {
      width: '100%',
      aspectRatio: '1',
      objectFit: 'cover',
      borderRadius: 8,
      border: '1px solid #2a4258',
    },
    addPhotoBtn: {
      width: '100%',
      aspectRatio: '1',
      border: '2px dashed #2a4258',
      borderRadius: 8,
      background: 'transparent',
      color: '#4fd1ff',
      fontSize: 28,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    canvas: {
      width: '100%',
      height: 200,
      background: '#fff',
      borderRadius: 8,
      touchAction: 'none',
      display: 'block',
    },
    center: { textAlign: 'center' },
    textarea: {
      width: '100%',
      padding: '12px 14px',
      background: '#0a1420',
      border: '1px solid #2a4258',
      borderRadius: 8,
      color: '#e8edf2',
      fontSize: 14,
      fontFamily: "'DM Mono', monospace",
      boxSizing: 'border-box',
      resize: 'vertical',
      minHeight: 70,
      marginTop: 16,
    },
  };

  const googleMapsUrl = ticket && ticket.address
    ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(ticket.address)
    : null;

  if (linkState) {
    const messages = {
      expired: { title: 'Link Expired', body: 'This task link has expired. Please contact the vendor for a new one.' },
      revoked: { title: 'Link No Longer Active', body: 'This link has been revoked. Please contact the vendor for a new link.' },
      completed: { title: 'Task Already Completed', body: 'This task has already been submitted. No further action is needed.' },
      invalid: { title: 'Invalid Link', body: 'This link is not valid. Please check the link and try again.' },
    };
    const m = messages[linkState];
    return (
      <div style={styles.page}>
        <div style={styles.logo}>IRONWALL</div>
        <div style={styles.tagline}>SERVICE BRIDGE</div>
        <div style={Object.assign({}, styles.card, styles.center)}>
          <h2 style={{ color: '#e8edf2', marginTop: 0 }}>{m.title}</h2>
          <p style={{ color: '#9db3c4', fontSize: 14 }}>{m.body}</p>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div style={styles.page}>
        <div style={styles.logo}>IRONWALL</div>
        <div style={styles.tagline}>SERVICE BRIDGE</div>
        <div style={styles.card}>
          <p style={{ color: '#9db3c4', fontSize: 14, marginTop: 0 }}>
            You have been assigned a task. Enter your registered phone number to view the details.
          </p>
          <form onSubmit={handleVerify}>
            <label style={styles.label}>Phone Number</label>
            <input
              style={styles.input}
              type="tel"
              inputMode="numeric"
              placeholder="10-digit mobile number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={10}
            />
            {error && <div style={styles.error}>{error}</div>}
            <button style={styles.button} type="submit" disabled={loading}>
              {loading ? 'Verifying...' : 'View Task'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'ticket' && ticket) {
    return (
      <div style={styles.page}>
        <div style={styles.logo}>IRONWALL</div>
        <div style={styles.tagline}>SERVICE BRIDGE</div>
        <div style={styles.card}>
          <h3 style={{ color: '#e8edf2', marginTop: 0 }}>Task #{ticket.ticket_number}</h3>
          <div style={styles.ticketRow}>
            <span style={styles.ticketLabel}>Type</span>
            <span style={styles.ticketValue}>{ticket.ticket_type}</span>
          </div>
          <div style={styles.ticketRow}>
            <span style={styles.ticketLabel}>Customer</span>
            <span style={styles.ticketValue}>{ticket.customer_name}</span>
          </div>
          <div style={styles.ticketRow}>
            <span style={styles.ticketLabel}>Contact</span>
            <span style={styles.ticketValue}>{ticket.customer_phone}</span>
          </div>
          <div style={styles.ticketRow}>
            <span style={styles.ticketLabel}>Address</span>
            <span style={styles.ticketValue}>{ticket.address}</span>
          </div>
          {ticket.scheduled_date && (
            <div style={styles.ticketRow}>
              <span style={styles.ticketLabel}>Scheduled</span>
              <span style={styles.ticketValue}>
                {new Date(ticket.scheduled_date).toLocaleDateString('en-IN')} {ticket.scheduled_slot}
              </span>
            </div>
          )}
          {ticket.product_description && (
            <div style={styles.ticketRow}>
              <span style={styles.ticketLabel}>Description</span>
              <span style={styles.ticketValue}>{ticket.product_description}</span>
            </div>
          )}
          {ticket.vendor_instructions && (
            <div style={styles.instructionsBox}>
              <strong style={{ color: '#4fd1ff' }}>Instructions from vendor:</strong>
              <br />
              {ticket.vendor_instructions}
            </div>
          )}

          {googleMapsUrl && (
            <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <button style={styles.buttonSecondary} type="button">Get Directions</button>
            </a>
          )}

          {error && <div style={styles.error}>{error}</div>}
          <button style={styles.button} onClick={handleStartTask} disabled={loading}>
            {loading ? 'Starting...' : 'Start Task'}
          </button>
          <button
            style={{ ...styles.buttonSecondary, borderColor: 'transparent', color: '#9db3c4' }}
            type="button"
            onClick={() => { setError(''); setStep('visitnote'); }}
          >
            Can't Complete This Task
          </button>
        </div>
      </div>
    );
  }

  if (step === 'photos') {
    return (
      <div style={styles.page}>
        <div style={styles.logo}>IRONWALL</div>
        <div style={styles.tagline}>SERVICE BRIDGE</div>
        <div style={styles.card}>
          <h3 style={{ color: '#e8edf2', marginTop: 0 }}>Add Photos</h3>
          <p style={{ color: '#9db3c4', fontSize: 13.5 }}>
            Take photos of the completed work ({photos.length}/{MAX_PHOTOS})
          </p>

          <div style={styles.photoGrid}>
            {photos.map((p) => (
              <img key={p.id} src={p.url} alt="proof" style={styles.photoThumb} />
            ))}
            {photos.length < MAX_PHOTOS && (
              <button
                style={styles.addPhotoBtn}
                type="button"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={uploadingPhoto}
              >
                {uploadingPhoto ? '...' : '+'}
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handlePhotoSelect}
          />

          {error && <div style={styles.error}>{error}</div>}
          <button style={styles.button} onClick={() => setStep('signature')}>
            Continue to Signature
          </button>
        </div>
      </div>
    );
  }

  if (step === 'signature') {
    return (
      <div style={styles.page}>
        <div style={styles.logo}>IRONWALL</div>
        <div style={styles.tagline}>SERVICE BRIDGE</div>
        <div style={styles.card}>
          <h3 style={{ color: '#e8edf2', marginTop: 0 }}>Customer Signature</h3>
          <p style={{ color: '#9db3c4', fontSize: 13.5 }}>
            Please ask the customer to sign below to confirm completion.
          </p>
          <canvas ref={canvasRef} width={400} height={200} style={styles.canvas} />
          <button style={styles.buttonSecondary} type="button" onClick={handleClearSignature}>
            Clear
          </button>

          <label style={Object.assign({}, styles.label, { marginTop: 16 })}>Notes (optional)</label>
          <textarea
            style={styles.textarea}
            placeholder="Any notes about the job, e.g. access details, issues faced..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {error && <div style={styles.error}>{error}</div>}
          <button style={styles.button} onClick={handleSubmitSignature} disabled={loading}>
            {loading ? 'Submitting...' : 'Submit & Complete Task'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'visitnote') {
    return (
      <div style={styles.page}>
        <div style={styles.logo}>IRONWALL</div>
        <div style={styles.tagline}>SERVICE BRIDGE</div>
        <div style={styles.card}>
          <h3 style={{ color: '#e8edf2', marginTop: 0 }}>Can't Complete This Task</h3>
          <p style={{ color: '#9db3c4', fontSize: 13.5 }}>
            Let the vendor know what happened. Your link stays active, so you
            can come back and finish this task once you're able to.
          </p>

          <label style={styles.label}>What happened?</label>
          <textarea
            style={styles.textarea}
            placeholder="e.g. Customer wasn't home, site was locked, missing material..."
            value={visitNoteText}
            onChange={(e) => setVisitNoteText(e.target.value)}
          />

          <label style={{ ...styles.label, marginTop: 16 }}>Photo (optional)</label>
          {visitNotePhoto ? (
            <div style={{ fontSize: 13, color: '#9db3c4', marginBottom: 8 }}>
              {visitNotePhoto.name}
            </div>
          ) : null}
          <button
            style={styles.buttonSecondary}
            type="button"
            onClick={() => visitNoteFileInputRef.current && visitNoteFileInputRef.current.click()}
          >
            {visitNotePhoto ? 'Change Photo' : 'Add Photo'}
          </button>
          <input
            ref={visitNoteFileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleVisitNotePhotoSelect}
          />

          {error && <div style={styles.error}>{error}</div>}
          <button style={styles.button} onClick={handleSubmitVisitNote} disabled={submittingNote}>
            {submittingNote ? 'Submitting...' : 'Submit Note'}
          </button>
          <button
            style={{ ...styles.buttonSecondary, borderColor: 'transparent', color: '#9db3c4' }}
            type="button"
            onClick={() => { setError(''); setStep('ticket'); }}
          >
            Back to Task
          </button>
        </div>
      </div>
    );
  }

  if (step === 'note-done') {
    return (
      <div style={styles.page}>
        <div style={styles.logo}>IRONWALL</div>
        <div style={styles.tagline}>SERVICE BRIDGE</div>
        <div style={Object.assign({}, styles.card, styles.center)}>
          <h2 style={{ color: '#e8edf2', marginTop: 0 }}>Note Submitted</h2>
          <p style={{ color: '#9db3c4', fontSize: 14 }}>
            The vendor has been notified. This link is still active — you can
            return to it anytime once you're able to complete the task.
          </p>
          <button
            style={{ ...styles.button, marginTop: 8 }}
            type="button"
            onClick={() => { setVisitNoteText(''); setVisitNotePhoto(null); setStep('ticket'); }}
          >
            Back to Task
          </button>
        </div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div style={styles.page}>
        <div style={styles.logo}>IRONWALL</div>
        <div style={styles.tagline}>SERVICE BRIDGE</div>
        <div style={Object.assign({}, styles.card, styles.center)}>
          <h2 style={{ color: '#e8edf2', marginTop: 0 }}>Task Completed</h2>
          <p style={{ color: '#9db3c4', fontSize: 14 }}>
            Thank you! The task has been marked as completed and the vendor has been notified.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default TaskPage;
